/**
 * Display state for an agent turn.
 *
 * A run can end without any streamed answer text — tool-only turns, a Stop, a
 * crash, or the Deep Dive time cap. The bubble starts life as a placeholder, so
 * anything that ends a turn has to replace it or the message keeps rendering a
 * loader long after the run is over.
 */

/** Placeholder content an agent bubble is created with, before any answer streams in. */
export const AGENT_WORKING_PLACEHOLDER = 'Agent is working...'

/** Shown when a turn ended with work done but no answer written up. */
export const AGENT_NO_ANSWER_NOTICE =
  "This run stopped before writing up an answer. The work it completed is shown above — ask again to pick it back up."

/** Shown when a turn ended with neither an answer nor any work to show. */
export const AGENT_NO_RESULT_NOTICE =
  "I wasn't able to finish that request. Please ask again and I'll pull it together right away."

export type AgentDisplayStatus = 'idle' | 'running' | 'completed' | 'failed' | 'cancelled'

const TERMINAL_STATUSES = new Set(['completed', 'failed', 'cancelled'])

/** True once a turn can no longer produce output, so the loader must be gone. */
export const isTerminalAgentStatus = (status: unknown): boolean => {
  const value = String(status || '')
  return TERMINAL_STATUSES.has(value) || value === 'interrupted'
}

/**
 * Map a persisted `analytics.agentStatus` onto a status the chain-of-thought UI
 * understands. `interrupted` has no UI state of its own, and a stored `running`
 * is always stale by the time it is read back — both read as cancelled so a
 * reloaded conversation never shows a live spinner.
 */
export const toDisplayAgentStatus = (persistedStatus: unknown, hasSteps: boolean): AgentDisplayStatus => {
  if (!hasSteps) return 'idle'
  const status = String(persistedStatus || '')
  if (TERMINAL_STATUSES.has(status)) return status as AgentDisplayStatus
  if (status === 'interrupted' || status === 'running') return 'cancelled'
  return 'completed'
}

/**
 * Whether a restored assistant turn is worth rendering. An interrupted run can
 * leave an answer-less turn whose chain of thought survived; dropping it hid the
 * turn entirely and reloaded the conversation blank.
 */
export const shouldRenderRestoredAssistantTurn = (content: unknown, stepCount: number): boolean =>
  Boolean(String(content || '').trim()) || stepCount > 0

/**
 * Content for an agent bubble whose turn just finished. Keeps streamed text or
 * any real content already on the message, and only swaps out the placeholder.
 */
export const resolveCompletedAgentContent = ({
  streamedResponse,
  currentContent,
  hasSteps,
}: {
  streamedResponse?: string | null
  currentContent?: string | null
  hasSteps?: boolean
}): string => {
  if (String(streamedResponse || '').trim()) return String(streamedResponse)
  const existing = String(currentContent || '')
  if (existing.trim() && existing !== AGENT_WORKING_PLACEHOLDER) return existing
  return hasSteps ? AGENT_NO_ANSWER_NOTICE : AGENT_NO_RESULT_NOTICE
}
